import { useDebouncedValue } from '@mantine/hooks';
import { cloneDeep, isEqual, omit } from 'lodash';
import type {
  ClaimRuleEvaluationInput,
  RuleEvaluationResult,
} from '@billing/validation/rules';
import { evaluateClaimRules } from 'api/resource/claim';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Claim, CreateClaimForm, EditClaimForm } from 'interfaces';
import { normalizeClaimRuleEvaluationData } from 'utils/claimRuleEvaluationUtils';

export type ClaimRuleFormData = Pick<Claim, 'siteID'> &
  (Partial<CreateClaimForm> | Partial<EditClaimForm>);

export interface LiveClaimRulesState {
  evaluation: RuleEvaluationResult | null;
  error: Error | null;
  isChecking: boolean;
  isStale: boolean;
  scheduleEvaluation: (claimData: ClaimRuleFormData) => void;
  setEvaluationBaseline: (
    claimData: ClaimRuleFormData,
    appliedFields?: string[]
  ) => void;
}

const getTopLevelField = (field: string): string => field.split('.')[0];
const NO_IGNORED_FIELDS: readonly string[] = [];

const getChangedTopLevelFields = (
  current: Record<string, unknown> | null,
  next: Record<string, unknown>
): string[] => {
  if (!current) return Object.keys(next);

  const fields = new Set([...Object.keys(current), ...Object.keys(next)]);
  return [...fields].filter((field) => !isEqual(current[field], next[field]));
};

export const useLiveClaimRules = ({
  claimID,
  debounceMilliseconds = 500,
  ignoredFields = NO_IGNORED_FIELDS,
}: {
  claimID?: string;
  debounceMilliseconds?: number;
  ignoredFields?: readonly string[];
}): LiveClaimRulesState => {
  const [claimData, setClaimData] = useState<ClaimRuleFormData | null>(null);
  const [debouncedClaimData] = useDebouncedValue(
    claimData,
    debounceMilliseconds
  );
  const [evaluation, setEvaluation] = useState<RuleEvaluationResult | null>(
    null
  );
  const [error, setError] = useState<Error | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [isStale, setIsStale] = useState(false);
  const latestRequestIDRef = useRef(0);
  const lastScheduledClaimDataRef = useRef<Record<string, unknown> | null>(
    null
  );
  const pendingAppliedFieldsRef = useRef<Set<string>>(new Set());

  const setEvaluationBaseline = useCallback(
    (nextClaimData: ClaimRuleFormData, appliedFields: string[] = []) => {
      const claimDataSnapshot = omit(cloneDeep(nextClaimData), ignoredFields);
      lastScheduledClaimDataRef.current =
        normalizeClaimRuleEvaluationData(claimDataSnapshot);
      pendingAppliedFieldsRef.current = new Set(
        appliedFields.map(getTopLevelField)
      );
    },
    [ignoredFields]
  );

  const scheduleEvaluation = useCallback(
    (nextClaimData: ClaimRuleFormData) => {
      const claimDataSnapshot = omit(cloneDeep(nextClaimData), ignoredFields);
      const normalizedClaimData =
        normalizeClaimRuleEvaluationData(claimDataSnapshot);
      if (isEqual(lastScheduledClaimDataRef.current, normalizedClaimData)) {
        return;
      }

      const changedFields = getChangedTopLevelFields(
        lastScheduledClaimDataRef.current,
        normalizedClaimData
      );
      const pendingAppliedFields = pendingAppliedFieldsRef.current;
      pendingAppliedFieldsRef.current = new Set();
      if (
        changedFields.length > 0 &&
        changedFields.every((field) => pendingAppliedFields.has(field))
      ) {
        lastScheduledClaimDataRef.current = normalizedClaimData;
        return;
      }

      lastScheduledClaimDataRef.current = normalizedClaimData;
      latestRequestIDRef.current += 1;
      setClaimData({ ...normalizedClaimData, siteID: nextClaimData.siteID });
      setIsStale(true);
      setError(null);
    },
    [ignoredFields]
  );

  useEffect(() => {
    if (!debouncedClaimData) return;

    const requestID = ++latestRequestIDRef.current;
    let cancelled = false;
    const isObsoleteRequest = () =>
      cancelled || requestID !== latestRequestIDRef.current;
    const input: ClaimRuleEvaluationInput = {
      siteID: debouncedClaimData.siteID,
      claimID,
      claimData: { ...debouncedClaimData },
    };

    const evaluateRules = async () => {
      setIsChecking(true);

      try {
        const result = await evaluateClaimRules(input);

        if (isObsoleteRequest()) return;
        setEvaluation(result);
        setError(null);
        setIsStale(false);
      } catch (caughtError: unknown) {
        if (isObsoleteRequest()) return;
        setEvaluation(null);
        const statefulError =
          caughtError instanceof Error
            ? caughtError
            : new Error('Rule evaluation failed.');
        setError(statefulError);
        setIsStale(false);
      } finally {
        if (!isObsoleteRequest()) {
          setIsChecking(false);
        }
      }
    };

    void evaluateRules();

    return () => {
      cancelled = true;
    };
  }, [claimID, debouncedClaimData]);

  return {
    evaluation,
    error,
    isChecking,
    isStale,
    scheduleEvaluation,
    setEvaluationBaseline,
  };
};
